'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';
import salsify from 'salsify-experiences-sdk';

const SALSIFY_ORIGIN = 'salsify';
const WEB_COLLAGE_SITECODE = 'petsmart';
const WEB_COLLAGE_CHECK_TIMEOUT = 2 * 1000;
const WEB_COLLAGE_CONTENT_SETTINGS = { 'power-page': { autoPlayAndStop: true, reload: true } };
const WEB_COLLAGE_SUBDOM_SELECTOR = 'syndigo-powerpage'; // HTML tag that Webcollage uses to render the content

export type UseSalsifyProps = {
    productId: string;
    salsifyClientId: string;
    salsifyIdType: string;
};

export function useSalsify({ salsifyClientId, salsifyIdType, productId }: UseSalsifyProps) {
    const [webCollageFound, setWebCollageFound] = useState(true);
    const [salsifyFound, setSalsifyFound] = useState(true);

    const initializeSalsify = useCallback(() => {
        const checkProduct = async () => {
            const exists = await salsify.enhancedContent.exists(productId);

            if (exists) {
                const salsifyTargetDiv = document.querySelector('#salsify-target') as HTMLDivElement;

                if (salsifyTargetDiv) {
                    salsify.enhancedContent.renderIframe(salsifyTargetDiv, productId);
                }
            }

            setSalsifyFound(Boolean(exists));
        };

        if (salsifyClientId && salsifyIdType) {
            salsify.init({
                clientId: salsifyClientId,
                enhancedContent: { idType: salsifyIdType },
            });

            // Check if enhanced content exists for product
            checkProduct();

            // Listen for messages from Salsify to adjust iframe to fit content
            window.addEventListener('message', event => {
                if (event.origin.includes(SALSIFY_ORIGIN)) {
                    const salsifyEcIframe: HTMLIFrameElement = document.getElementById(
                        'salsify-ec-iframe'
                    ) as HTMLIFrameElement;

                    if (salsifyEcIframe) {
                        salsifyEcIframe.height = event.data.height as string;
                        salsifyEcIframe.style.height = `${event.data.height}px`;
                    }
                }
            });
        }
    }, [productId, salsifyClientId, salsifyIdType]);

    // Effect to handle initial setup and re-initialization if IDs change
    useEffect(() => {
        if (!window.flags?.IS_PERFORMANCE_MODE && !salsify.initialized) {
            initializeSalsify();
        }
    }, [initializeSalsify, salsifyClientId, salsifyIdType]);

    // Initialize Webcollage content
    const initializeWebcollage = useCallback(() => {
        if (window.flags?.IS_PERFORMANCE_MODE) return;

        if (window.Webcollage) {
            window.Webcollage.loadProductContent(WEB_COLLAGE_SITECODE, String(productId), WEB_COLLAGE_CONTENT_SETTINGS);

            setTimeout(() => {
                const webCollageDiv = document.querySelector(WEB_COLLAGE_SUBDOM_SELECTOR) as HTMLDivElement;

                setWebCollageFound(Boolean(webCollageDiv));
            }, WEB_COLLAGE_CHECK_TIMEOUT);
        }
    }, []);

    return useMemo(
        () => ({ initializeWebcollage, webCollageFound, salsifyFound }),
        [initializeWebcollage, webCollageFound, salsifyFound]
    );
}
